/**
 * OnboardingTemplatePanelV2 Component
 *
 * Right sidebar panel displaying draggable V2 StepNode templates.
 * Uses V2ScreenView from @modules/screen-view-v2 for visual previews.
 *
 * V2-native template panel:
 * - Templates are native StepNode objects (from flowDocument.screens)
 * - Previews rendered with V2ScreenView (not V1 ScreenView)
 * - Gear icon opens ThemeEditorDialogV2 (not V1 ThemeEditorDialog)
 * - Theme settings loaded from themeSettingsV2 subcollection
 * - Fallback to defaultStepNodeFactory templates
 */

import React, { useState, useMemo, useCallback, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  TextField,
  List,
  ListItem,
  Typography,
  InputAdornment,
  IconButton,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import { useTheme } from '@mui/material/styles';
import type { StepNode } from '@segmently/schema-models/v2';
import { V2ScreenView } from '@modules/screen-view-v2';
import { useOnboardingThemeSettingsV2 } from '@modules/onboardings/hooks/useOnboardingThemeSettingsV2';
import { ThemeEditorDialogV2, type ThemeEditorDialogV2Props } from '@modules/themes/components/ThemeEditorDialogV2';
import type { OnboardingThemeSettingsV2 } from '@modules/firebase-core/converters/onboardingConverter';
import { WithGuideHelp } from '@modules/ui-components';
import { GuideService } from '@modules/firebase-core/services/GuideService';
import { GUIDE_ANCHORS } from '@modules/guides/anchors/guideAnchorRegistry';
import {
  TEMPLATE_PANEL_CONFIG,
  TEMPLATE_PREVIEW_CONFIG,
  SCREEN_DESIGN_SIZE,
} from '@modules/reactFlow/constants/screenPreviewConfig';
import { getDefaultV2TemplateScreens } from '@modules/reactFlow/services/screen/mockTemplateScreensV2';
import { ALLOWED_STEP_KINDS_ORDER } from '@modules/reactFlow/config/v2ScreenTypeConfig';

const CANVAS_ADD_SCREEN_ANCHOR = GUIDE_ANCHORS.canvasAddScreen;

/**
 * OnboardingTemplatePanelV2 Handle - imperative methods exposed via ref
 */
export interface OnboardingTemplatePanelV2Handle {
  /** Get V2 template by ID for drag-drop operations */
  getTemplateById: (id: string) => StepNode | null;
  /** All available templates (for the click-to-add screen picker). */
  getTemplates: () => StepNode[];
  /**
   * Persist a new template (a subtyped clone) into THIS onboarding's theme
   * (themeSettingsV2.flowDocument.screens). Scope is the current onboarding only.
   */
  addThemeTemplate: (node: StepNode) => Promise<void>;
  /**
   * Re-read THIS onboarding's theme from Firestore. The parent calls it after it replaced the theme
   * elsewhere (the admin theme switch), so the templates show the new theme without a reload.
   */
  refreshThemeSettings: () => Promise<void>;
}

/**
 * OnboardingTemplatePanelV2 Props
 */
export interface OnboardingTemplatePanelV2Props {
  /** Project ID for loading V2 theme settings from subcollection */
  projectId?: string;
  /** Onboarding ID for loading V2 theme settings from subcollection */
  onboardingId?: string;
  /** Version ID for screen editor */
  versionId?: string;
  /** Default language for screen editor */
  defaultLanguage?: string;
  /** Languages for screen editor */
  languages?: string[];
  /** Array of V2 template screens (optional, overrides lazy-loaded templates) */
  templates?: StepNode[];
  /** Callback when template drag starts */
  onTemplateDragStart?: (template: StepNode) => void;
  /** Callback when template drag ends */
  onTemplateDragEnd?: () => void;
  /** External theme settings for editing (strategy mode — bypasses useOnboardingThemeSettingsV2) */
  externalThemeSettings?: OnboardingThemeSettingsV2 | null;
  /** Callback to save external theme settings (strategy mode) */
  onExternalThemeSettingsSave?: (updated: OnboardingThemeSettingsV2) => Promise<void>;
  /** Opt-in: selective theme apply to canvas (forwarded to ThemeEditorDialogV2) */
  onApplyThemeToCanvas?: ThemeEditorDialogV2Props['onApplyThemeToCanvas'];
  /** Opt-in: click a template to add it to the canvas (additive to drag-and-drop). */
  onTemplateClick?: (templateId: string) => void;
}

/**
 * OnboardingTemplatePanelV2 Component
 *
 * Right sidebar with draggable V2 StepNode templates
 */
export const OnboardingTemplatePanelV2 = forwardRef<OnboardingTemplatePanelV2Handle, OnboardingTemplatePanelV2Props>(({
  projectId = '',
  onboardingId = '',
  versionId = '',
  defaultLanguage = 'en-US',
  languages = ['en-US'],
  templates: externalTemplates,
  onTemplateDragStart,
  onTemplateDragEnd,
  externalThemeSettings,
  onExternalThemeSettingsSave,
  onApplyThemeToCanvas,
  onTemplateClick,
}, ref) => {
  const theme = useTheme();
  const guideService = useMemo(() => new GuideService(), []);

  // Skip loading if no projectId/onboardingId (e.g., wizard context)
  const shouldLoadFromSubcollection = !!(projectId && onboardingId);

  // Lazy load V2 theme settings from subcollection
  const {
    themeSettings: themeSettingsV2,
    loading: themeSettingsLoading,
    update: updateThemeSettings,
    refresh: refreshThemeSettings,
  } = useOnboardingThemeSettingsV2({
    projectId,
    onboardingId,
    skipInitialLoad: !shouldLoadFromSubcollection,
  });

  // Theme editor dialog state
  const [editorOpen, setEditorOpen] = useState(false);

  // Effective theme settings: external (strategy mode) or loaded from subcollection (onboarding mode)
  const effectiveThemeSettings = externalThemeSettings ?? themeSettingsV2;

  // Get templates from: 1) external prop, 2) effective V2 theme settings flowDocument, 3) fallback to mock V2.
  const templates = useMemo(() => {
    // External templates take priority (for wizard)
    if (externalTemplates) {
      return externalTemplates;
    }

    // Use flowDocument screens from the selected theme source.
    if (effectiveThemeSettings?.flowDocument?.screens) {
      return Object.values(effectiveThemeSettings.flowDocument.screens) as StepNode[];
    }

    // Fallback to default V2 templates from factory
    return getDefaultV2TemplateScreens();
  }, [effectiveThemeSettings, externalTemplates]);

  // Expose imperative handle for parent component to access templates by ID
  useImperativeHandle(ref, () => ({
    getTemplateById: (id: string): StepNode | null => {
      return templates.find(t => t.id === id) || null;
    },
    getTemplates: (): StepNode[] => templates,
    addThemeTemplate: async (node: StepNode): Promise<void> => {
      const base = effectiveThemeSettings;
      if (!base) return; // no theme to extend (shouldn't happen in onboarding mode)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const fd = (base.flowDocument ?? { screens: {} }) as any;
      const updated = {
        ...base,
        flowDocument: {
          ...fd,
          screens: { ...(fd.screens ?? {}), [node.id]: node },
        },
      } as OnboardingThemeSettingsV2;
      // Onboarding mode persists to the subcollection; strategy mode routes out.
      if (onExternalThemeSettingsSave) {
        await onExternalThemeSettingsSave(updated);
      } else {
        await updateThemeSettings(updated);
      }
    },
    refreshThemeSettings: async (): Promise<void> => {
      if (externalThemeSettings || !shouldLoadFromSubcollection) return;
      await refreshThemeSettings();
    },
  }), [
    templates,
    effectiveThemeSettings,
    externalThemeSettings,
    onExternalThemeSettingsSave,
    refreshThemeSettings,
    shouldLoadFromSubcollection,
    updateThemeSettings,
  ]);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Handle settings button click
  const handleSettingsClick = useCallback(() => {
    if (!effectiveThemeSettings) return;
    // Re-read the saved theme so the editor opens with the latest values even if
    // the theme was changed elsewhere (e.g. the canvas "Edit theme & apply" flow).
    if (!externalThemeSettings) {
      void refreshThemeSettings();
    }
    setEditorOpen(true);
  }, [effectiveThemeSettings, externalThemeSettings, refreshThemeSettings]);

  // Handle V2 theme settings save from dialog
  const handleThemeSettingsSave = useCallback(async (updated: OnboardingThemeSettingsV2) => {
    if (onExternalThemeSettingsSave) {
      await onExternalThemeSettingsSave(updated);
    } else {
      await updateThemeSettings(updated);
    }
  }, [updateThemeSettings, onExternalThemeSettingsSave]);

  // Filter templates by allowed StepKinds, apply search, and sort by defined order
  const filteredTemplates = useMemo(() => {
    // Step 1: Filter by allowed StepKinds (whitelist)
    let filtered = templates.filter(
      (template) => template.screenType && template.screenType in ALLOWED_STEP_KINDS_ORDER
    );

    // Step 2: Apply search filter if query exists
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (template) =>
          (template.name as string | undefined)?.toLowerCase().includes(query) ||
          template.screenType?.toLowerCase().includes(query)
      );
    }

    // Step 3: Sort by defined order
    filtered.sort((a, b) => {
      const orderA = ALLOWED_STEP_KINDS_ORDER[a.screenType || ''] ?? 999;
      const orderB = ALLOWED_STEP_KINDS_ORDER[b.screenType || ''] ?? 999;
      return orderA - orderB;
    });

    return filtered;
  }, [templates, searchQuery]);

  // Handle drag start
  const handleDragStart = (template: StepNode) => (event: React.DragEvent) => {
    event.dataTransfer.effectAllowed = 'copy';
    const dragData = {
      templateId: template.id,
      entityType: 'onboarding-screen-v2',
    };
    event.dataTransfer.setData('application/json', JSON.stringify(dragData));

    if (onTemplateDragStart) {
      onTemplateDragStart(template);
    }
  };

  // Handle drag end
  const handleDragEnd = () => {
    if (onTemplateDragEnd) {
      onTemplateDragEnd();
    }
  };

  return (
    <Box
      data-testid="onboarding-template-panel-v2"
      role="complementary"
      aria-label="V2 Template Panel"
      sx={{
        width: TEMPLATE_PANEL_CONFIG.PANEL_WIDTH,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: theme.palette.background.paper,
        borderLeft: `2px solid ${theme.palette.divider}`,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          height: 48,
          px: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {/* Left spacer for centering title */}
        <Box sx={{ width: 32 }} />

        {/* Center: Title */}
        <WithGuideHelp
          guideKey={CANVAS_ADD_SCREEN_ANCHOR.guideKey}
          guideService={guideService}
          data-testid={CANVAS_ADD_SCREEN_ANCHOR.testId}
          position="top-right"
        >
          <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600 }}>
            Screen Templates
          </Typography>
        </WithGuideHelp>

        {/* Right: Settings button */}
        <Tooltip title={effectiveThemeSettings ? 'Edit V2 theme settings' : 'No V2 theme settings'}>
          <span>
            <IconButton
              size="small"
              aria-label="Template settings"
              onClick={handleSettingsClick}
              disabled={!effectiveThemeSettings || themeSettingsLoading}
              data-testid="onboarding-template-theme-settings-btn"
            >
              <SettingsIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Search Field */}
      <Box sx={{ padding: 2, paddingBottom: 1 }}>
        <TextField
          data-testid="onboarding-template-search-v2"
          fullWidth
          size="small"
          placeholder="Search templates"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          inputProps={{
            'aria-label': 'Search templates',
          }}
        />
      </Box>

      {/* Templates List */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          padding: 2,
          paddingTop: 1,
        }}
      >
        {filteredTemplates.length === 0 ? (
          // No results message
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 4,
              textAlign: 'center',
            }}
          >
            <Typography data-testid="onboarding-template-no-results-v2" variant="body2" color="text.secondary">
              No results found
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              Try different keywords
            </Typography>
          </Box>
        ) : (
          // Templates list
          <List data-testid="onboarding-template-list-v2" sx={{ padding: 0 }}>
            {filteredTemplates.map((template) => (
              <ListItem
                key={template.id}
                data-testid={`onboarding-template-item-v2-${template.screenType}`}
                data-template-id={template.id}
                draggable
                onDragStart={handleDragStart(template)}
                onDragEnd={handleDragEnd}
                onClick={onTemplateClick ? () => onTemplateClick(template.id) : undefined}
                sx={{
                  padding: 0,
                  marginBottom: `${TEMPLATE_PANEL_CONFIG.ITEM_SPACING}px`,
                  cursor: onTemplateClick ? 'pointer' : 'grab',
                  '&:active': {
                    cursor: 'grabbing',
                  },
                  '&:hover': {
                    opacity: 0.8,
                  },
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                  }}
                >
                  {/* Template Type */}
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                      textAlign: 'center',
                    }}
                  >
                    {template.screenType || template.id}
                  </Typography>

                  {/* Template Preview Container */}
                  <Box
                    data-testid={`onboarding-template-preview-v2-${template.id}`}
                    sx={{
                      width: `${TEMPLATE_PREVIEW_CONFIG.CONTAINER_WIDTH}px`,
                      height: `${TEMPLATE_PREVIEW_CONFIG.CONTAINER_HEIGHT}px`,
                      margin: '0 auto',
                      position: 'relative',
                      background: theme.palette.background.default,
                      borderRadius: '8px',
                      border: `1px solid ${theme.palette.divider}`,
                      overflow: 'hidden',
                    }}
                  >
                    {/* Preview content wrapper with V2ScreenView */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        width: SCREEN_DESIGN_SIZE.WIDTH,
                        height: SCREEN_DESIGN_SIZE.HEIGHT,
                        transform: `translate(-50%, -50%) scale(${TEMPLATE_PREVIEW_CONFIG.CONTAINER_SCALE * TEMPLATE_PREVIEW_CONFIG.PREVIEW_SCALE})`,
                        transformOrigin: 'center center',
                      }}
                    >
                      <V2ScreenView
                        step={template}
                        locale="en-US"
                        previewMode="static"
                      />
                    </Box>
                  </Box>
                </Box>
              </ListItem>
            ))}
          </List>
        )}

        {/* Loading indicator for V2 theme settings */}
        {themeSettingsLoading && shouldLoadFromSubcollection && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: 2,
            }}
          >
            <CircularProgress size={20} />
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              Loading V2 theme...
            </Typography>
          </Box>
        )}
      </Box>

      {/* V2 Theme Editor Dialog */}
      {effectiveThemeSettings && (
        <ThemeEditorDialogV2
          open={editorOpen}
          onClose={() => setEditorOpen(false)}
          themeSettings={effectiveThemeSettings}
          onSave={handleThemeSettingsSave}
          title="Edit V2 Theme Settings"
          defaultLanguage={defaultLanguage}
          languages={languages}
          projectId={projectId}
          onboardingId={onboardingId}
          versionId={versionId}
          onApplyThemeToCanvas={onApplyThemeToCanvas}
        />
      )}
    </Box>
  );
});

export default OnboardingTemplatePanelV2;
